import { createContext, type ReactNode, useCallback, useContext, useState } from "react";
import type { UseFilePickerOptions } from "@medbillai/ui";
import type { DocumentScanEntryPoint } from "@medbillai/utils/documentScan";
import { useScreenLock } from "../../lib/screen-lock";
import type { UploadError, UploadFile } from "../../provider/documents";
import { UploadDocumentSheet } from "./UploadDocumentSheet";
type PickerRequest = {
  title?: string;
  /** Overrides the sheet's "Upload a file" picker constraints (accept, multiple). */
  filePickerOptions?: Pick<UseFilePickerOptions, "accept" | "multiple">;
  onFilesLoaded: (files: UploadFile[]) => Promise<void>;
  onUploadError: (error: UploadError) => void;
};
type OpenDocumentPicker = (request: PickerRequest) => void;
const UploadDocumentPickerContext = createContext<OpenDocumentPicker | null>(null);
export const useUploadDocumentPicker = () => useContext(UploadDocumentPickerContext);
export const useRequiredUploadDocumentPicker = () => {
  const openDocumentPicker = useUploadDocumentPicker();
  if (!openDocumentPicker) {
    throw new Error("useRequiredUploadDocumentPicker must be used within UploadDocumentPickerProvider");
  }
  return openDocumentPicker;
};
export const UploadDocumentPickerProvider = ({
  children,
  issueId,
  entryPoint
}: {
  children: ReactNode;
  issueId?: string;
  entryPoint: DocumentScanEntryPoint;
}) => {
  const [request, setRequest] = useState<PickerRequest | null>(null);
  const {
    disableScreenLock,
    enableScreenLock
  } = useScreenLock();
  const openDocumentPicker = useCallback<OpenDocumentPicker>(nextRequest => setRequest(nextRequest), []);
  return <UploadDocumentPickerContext.Provider value={openDocumentPicker} data-sentry-element="UploadDocumentPickerContext.Provider" data-sentry-component="UploadDocumentPickerProvider" data-sentry-source-file="UploadDocumentPickerProvider.tsx">
      {children}
      <UploadDocumentSheet open={request !== null} onOpenChange={open => {
      if (!open) {
        setRequest(null);
      }
    }} onCameraPermissionCheckStart={disableScreenLock} onCameraPermissionCheckComplete={enableScreenLock} onUploadError={error => request?.onUploadError(error)} onFilesLoaded={async files => {
      const activeRequest = request;
      if (!activeRequest) {
        return;
      }
      setRequest(null);
      try {
        await activeRequest.onFilesLoaded(files);
      } catch (error) {
        // The sheet's error routing targets `request`, already cleared by
        // the close above — deliver load failures to the captured request.
        activeRequest.onUploadError({
          error: error instanceof Error ? error : new Error(String(error))
        });
      }
    }} title={request?.title} filePickerOptions={request?.filePickerOptions} issueId={issueId} entryPoint={entryPoint} data-sentry-element="UploadDocumentSheet" data-sentry-source-file="UploadDocumentPickerProvider.tsx" />
    </UploadDocumentPickerContext.Provider>;
};